import { IInventoryScanOption, TPodType, TScanSteps } from '../../../types';

export interface ITransferDetails {
  barcode: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferId: string;
  isDestinationScanned: boolean;
  isPod: boolean;
  packagesCount: number;
  sourcePod: string;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceZone: string;
  title: string;
  transferStatus: string;
  traysCount?: number;
  workStationId: string;
  workStationProcessId: string;
  workStationZoneType: string;
}

export type TTransferStatus = 'IN_PROGRESS' | 'RECEIVED';

export enum IInventoryTransferStatus {
  OPEN = 'OPEN', // Source scanned
  IN_PROGRESS = 'IN_PROGRESS', // Destination scanned
  CLOSED = 'CLOSED', // Done
  CANCELLED = 'CANCELLED', // Cancelled
  CANCELLATION_IN_PROGRESS = 'CANCELLATION_IN_PROGRESS',
}

/**
 * This interface represents the inventory transfer list data.
 */
export interface IInventoryTransferList {
  __v: number;
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: IInventoryTransferStatus;
  missingTrays: number;
  noOfTrays: number;
  sourcePod: TPodType;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export enum InventoryTransferError {
  DESTINATION_WORKSTATION_NOT_FOUND = 'DESTINATION_WORKSTATION_NOT_FOUND',
  DESTINATION_POD_NOT_FOUND = 'DESTINATION_POD_NOT_FOUND',
  SOURCE_TRAY_OR_POD_NOT_FOUND = 'SOURCE_TRAY_OR_POD_NOT_FOUND',
  SOURCE_WORKSTATION_NOT_FOUND = 'SOURCE_WORKSTATION_NOT_FOUND',
  SOURCE_POD_NOT_FOUND = 'SOURCE_POD_NOT_FOUND',
  SINGLE_TRAY_WORK_IN_PROGRESS = 'SINGLE_TRAY_WORK_IN_PROGRESS',
  MULTIPLE_TRAY_WORK_IN_PROGRESS = 'MULTIPLE_TRAY_WORK_IN_PROGRESS',
  MULTIPLE_OPEN_INBOUND_TRANSACTIONS = 'MULTIPLE_OPEN_INBOUND_TRANSACTIONS',
  SINGLE_OPEN_INBOUND_TRANSACTION = 'SINGLE_OPEN_INBOUND_TRANSACTION',
  SINGLE_ACTIVE_INVENTORY_TRANSFER = 'SINGLE_ACTIVE_INVENTORY_TRANSFER',
  MULTIPLE_ACTIVE_INVENTORY_TRANSFERS = 'MULTIPLE_ACTIVE_INVENTORY_TRANSFERS',
  MULTIPLE_POD_TYPES_NOT_ALLOWED = 'MULTIPLE_POD_TYPES_NOT_ALLOWED',
  ALL_TRAYS_MISSING = 'ALL_TRAYS_MISSING',
}

/**
 * This interface defines the structure of the Inbound Item.
 */
export interface IOpenInboundAssetItem {
  _id: string;
  courierPersonFullName: string;
  courierProvider: string;
  inventoryCount: number;
  inventoryTransactionId: string;
  transactionStatus: 'OPEN' | 'CLOSED';
  updatedBy: string;
  updatedDate: number;
}

export interface IScanPodOrTrayCodeErrorResponse {
  openInboundTransactionCount?: number;
  activeTransferDetails?: IInventoryTransferList;
  inboundTransactionDetails?: IOpenInboundAssetItem;
  trayInformation?: {
    trayName: string;
  };
  errorCode: InventoryTransferError;
}

/**
 * This state is used to track all steps and data associated with the
 * remove package process.
 */
export interface INewTransferStore {
  errorResponse: IScanPodOrTrayCodeErrorResponse | null;
  updateErrorResponse: (
    errorResponse: IScanPodOrTrayCodeErrorResponse | null
  ) => void;
  currentScanStep: TScanSteps;
  inventoryTransferId: string;
  isPodScan: boolean;
  isScannerReadingData: boolean;
  isCancelFlow: boolean;
  updateIsCancelFlow: (isCancelFlow: boolean) => void;
  isTrayListScanCompleted: boolean;
  resetStore: () => void;
  scanType: IInventoryScanOption;
  setIsTrayListScanCompleted: (isCompleted: boolean) => void;
  transferDetails: ITransferDetails | null;
  transferTrayFilter: TTransferStatus;
  updateCurrentScanStep: (currentScanStep: TScanSteps) => void;
  updateInventoryTransferId: (id: string) => void;
  updateIsPodScan: (isPodScan: boolean) => void;
  updateIsScannerReadingData: (isScannerReadingData: boolean) => void;
  updateScanType: (scanType: IInventoryScanOption) => void;
  updateTransferDetails: (transferDetails: ITransferDetails | null) => void;
  updateTransferTrayFilter: (transferTrayFilter: TTransferStatus) => void;
}
